import {createAsyncThunk} from '@reduxjs/toolkit';
import {
    getYoySummary,
    getYoyGraphs,
    getYoyBreakdown,
} from 'api/accounting/dashboard-yoy';
import {
    getMtdSummary,
    getMTDAggregatedReport,
    getMTDReport,
} from 'api/accounting/dashboard-mtd';

/** @namespace Dashboard.Actions */

/**
 * Thunk that fetches all YOY summary data .
 */
export const getYoySummaryData = createAsyncThunk(
    'dashboard/getYoySummaryData',
    /**
     * @param {Number} currentYear
     * @param {Array<Number>} facilityIds
     * @param {function} handleAbortSignal
     * @param {object} abortSignal
     * @param rejectWithValue
     * @param signal
     * @return {Promise<RejectWithValue<{} extends {rejectValue: infer RejectValue} ? RejectValue : unknown, {} extends {rejectedMeta: infer RejectedMeta} ? RejectedMeta : unknown>|AccountingDashboardReportSummary[]>}
     */
    async (
        {currentYear, facilityIds, currentDate, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getYoySummary(
                {
                    currentYear,
                    facilityIds,
                    currentDate,
                },
                {signal: abortSignal}
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/**
 * Thunk that fetches all YOY graphs data .
 */
export const getYoyGraphsData = createAsyncThunk(
    'dashboard/getYoyGraphsData',
    /**
     * @param {Number} currentYear
     * @param {Array<Number>} facilityIds
     * @param {function} handleAbortSignal
     * @param {object} abortSignal
     * @param rejectWithValue
     * @param signal
     * @return {Promise<RejectWithValue<{} extends {rejectValue: infer RejectValue} ? RejectValue : unknown, {} extends {rejectedMeta: infer RejectedMeta} ? RejectedMeta : unknown>|DashboardAggregatedResponse[]>}
     */
    async (
        {currentYear, facilityIds, currentDate, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getYoyGraphs(
                {
                    currentYear,
                    facilityIds,
                    currentDate,
                },
                {signal: abortSignal}
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/**
 * Thunk that fetches all YOY breakdown data .
 */
export const getYoyBreakdownData = createAsyncThunk(
    'dashboard/getYoyBreakdownData',
    /**
     * @param {Number} currentYear
     * @param {Array<Number>} facilityIds
     * @param {function} handleAbortSignal
     * @param {object} abortSignal
     * @param rejectWithValue
     * @param signal
     * @return {Promise<RejectWithValue<{} extends {rejectValue: infer RejectValue} ? RejectValue : unknown, {} extends {rejectedMeta: infer RejectedMeta} ? RejectedMeta : unknown>|AccountingReportYOY[]>}
     */
    async (
        {currentYear, facilityIds, currentDate, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getYoyBreakdown(
                {
                    currentYear,
                    facilityIds,
                    currentDate,
                },
                {signal: abortSignal}
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getMtdSummaryData = createAsyncThunk(
    'dashboard/getMtdSummaryData',
    /**
     * Asynchronously fetches MTD summary data from the API.
     *
     * @param {Object} options - The options for fetching data.
     * @param {string} options.dateTo - The end date for the data range.
     * @param {string} options.dateFrom - The start date for the data range.
     * @param {string} options.testMode - The start date for the data range.
     * @param {Array<number>} options.facilityIDs - An array of facility IDs to filter the data.
     * @param {Function} options.handleAbortSignal - A function to handle aborting the request.
     * @param {AbortController.prototype.signal} options.abortSignal - The cancel token to cancel the request.
     * @returns {} - A promise that resolves to the fetched data or rejects with an error.
     */
    async (
        {
            dateTo,
            dateFrom,
            testMode,
            facilityIDs,
            handleAbortSignal,
            abortSignal,
        },
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getMtdSummary(
                {
                    dateTo,
                    dateFrom,
                    testMode,
                    facilityIDs,
                },
                {signal: abortSignal}
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getMtdGraphData = createAsyncThunk(
    'dashboard/getMtdGraphData',
    /**
     * Asynchronously fetches MTD graph data from the API.
     *
     * @param {string} dateTo - The end date for the data range.
     * @param {string} dateFrom - The start date for the data range.
     * @param {string} testMode - The start date for the data range.
     * @param {Array<number>} facilityIDs - An array of facility IDs to filter the data.
     * @param rejectWithValue
     * @param signal
     * @param {Function} options.handleAbortSignal - A function to handle aborting the request.
     * @param {AbortController.prototype.signal} options.abortSignal - The cancel token to cancel the request.
     * @return {Promise<RejectWithValue<{} extends {rejectValue: infer RejectValue} ? RejectValue : unknown, {} extends {rejectedMeta: infer RejectedMeta} ? RejectedMeta : unknown>|DashboardAggregatedResponse[]>}
     */
    async (
        {
            dateTo,
            dateFrom,
            testMode,
            facilityIDs,
            handleAbortSignal,
            abortSignal,
        },
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getMTDAggregatedReport(
                {
                    dateTo,
                    dateFrom,
                    testMode,
                    facilityIDs,
                },
                {signal: abortSignal}
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const getMtdBreakdownData = createAsyncThunk(
    'dashboard/getMtdBreakdownData',
    /**
     * Asynchronously fetches MTD breakdown table data from the API.
     *
     * @param {Object} options - The options for fetching data.
     * @param {string} options.dateTo - The end date for the data range.
     * @param {string} options.dateFrom - The start date for the data range.
     * @param {string} options.testMode - The start date for the data range.
     * @param {Array<number>} options.facilityIDs - An array of facility IDs to filter the data.
     * @param {Function} options.handleAbortSignal - A function to handle aborting the request.
     * @param {AbortController.prototype.signal} options.abortSignal - The cancel token to cancel the request.
     * @returns {} - A promise that resolves to the fetched data or rejects with an error.
     */
    async (
        {
            dateTo,
            dateFrom,
            testMode,
            facilityIDs,
            perPage,
            handleAbortSignal,
            abortSignal,
        },
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getMTDReport(
                {
                    dateTo,
                    dateFrom,
                    perPage,
                    testMode,
                    facilityIDs,
                },
                {signal: abortSignal}
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);
